import React, { useState, useEffect, useRef } from 'react';
import { serviceService } from '../../services/serviceService';
import serviceClassificationService from '../../services/serviceClassificationService';
import { getPointOfSales } from '../../services/pointOfSalesService';
import { machineryService } from '../../services/machineryService';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import ClassificationSelector from '../Products/ClassificationSelector';
import Toast from '../Common/Toast';

const ServiceForm = ({ isOpen, onClose, service = null, onSuccess, isPageMode = false }) => {
  const { machineryEnabled, servicesClassificationEnabled, loaded: moduleSettingsLoaded } = useModuleSettings();
  const [formData, setFormData] = useState({
    name: '',
    service_type: 'acquisition',
    service_family_id: null,
    is_active: true,
    has_price_list: false,
    is_taxable: false,
    machinery_register_enabled: false,
    pos_prices: {}, // { point_of_sale_id: price }
    machinery_assets: [], // [{ machinery_asset_type, machinery_asset_id, usage_type?, code?, name? }]
  });

  const [pointOfSales, setPointOfSales] = useState([]);
  const [classificationHierarchy, setClassificationHierarchy] = useState([]);
  const [vehiclesList, setVehiclesList] = useState([]);
  const [machinesList, setMachinesList] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [skuPreview, setSkuPreview] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [loadingService, setLoadingService] = useState(false);
  const hasLoadedFormData = useRef(false);
  const hasLoadedService = useRef(false);

  // Cargar datos del formulario (unidades y puntos de venta)
  useEffect(() => {
    if (!moduleSettingsLoaded) return;
    if (!(isOpen || isPageMode)) return;
    if (!hasLoadedFormData.current) {
      hasLoadedFormData.current = true;
      loadFormData();
    }
  }, [isOpen, isPageMode, moduleSettingsLoaded]);

  useEffect(() => {
    if (!moduleSettingsLoaded || !servicesClassificationEnabled || !(isOpen || isPageMode)) {
      if (!servicesClassificationEnabled) setClassificationHierarchy([]);
      return;
    }
    serviceClassificationService
      .getFormData({ active_only: true })
      .then((res) => {
        if (res.success && res.data?.hierarchy) {
          setClassificationHierarchy(res.data.hierarchy);
        }
      })
      .catch(() => setClassificationHierarchy([]));
  }, [servicesClassificationEnabled, moduleSettingsLoaded, isOpen, isPageMode]);

  // Generar preview de SKU
  useEffect(() => {
    if (formData.service_type) {
      const prefix = formData.service_type === 'acquisition' ? 'SERV-ACQ' : 'SERV-SALE';
      const number = '0001'; // En producción, esto se calcularía
      setSkuPreview(`${prefix}-${number}`);
    }
  }, [formData.service_type]);

  // Cargar servicio si se pasa un ID (modo edición)
  useEffect(() => {
    if (service && typeof service === 'object' && service.id && !hasLoadedService.current) {
      hasLoadedService.current = true;
      loadServiceDataFromObject(service);
    }
  }, [service]);

  // Cargar vehículos y máquinas cuando el módulo Maquinaria está activo
  useEffect(() => {
    if (!machineryEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const [vRes, mRes] = await Promise.all([
          machineryService.getVehicles(),
          machineryService.getMachines(),
        ]);
        if (cancelled) return;
        if (vRes?.success && Array.isArray(vRes.data)) setVehiclesList(vRes.data);
        if (mRes?.success && Array.isArray(mRes.data)) setMachinesList(mRes.data);
      } catch {
        // ignorar
      }
    })();
    return () => { cancelled = true; };
  }, [machineryEnabled]);

  const loadFormData = async () => {
    try {
      const posResponse = await getPointOfSales();

      if (posResponse.success && Array.isArray(posResponse.data)) {
        const activePos = posResponse.data.filter(pos => pos.is_active);
        setPointOfSales(activePos);
      }
    } catch (error) {
      console.error('Error al cargar datos del formulario:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los datos del formulario',
        type: 'error'
      });
    }
  };

  const loadServiceDataFromObject = (serviceData) => {
    // Procesar precios por punto de venta
    const processedPosPrices = {};
    if (serviceData.pos_prices && typeof serviceData.pos_prices === 'object') {
      Object.keys(serviceData.pos_prices).forEach(key => {
        const posId = parseInt(key);
        if (!isNaN(posId) && posId > 0) {
          processedPosPrices[posId] = serviceData.pos_prices[key];
        }
      });
    }

    const serviceType = serviceData.service_type || 'acquisition';
    setFormData({
      name: serviceData.name || '',
      service_type: serviceType,
      service_family_id: serviceData.service_family_id ?? serviceData.family?.id ?? null,
      is_active: serviceData.is_active !== undefined ? serviceData.is_active : true,
      has_price_list: serviceType === 'sale' ? true : false,
      is_taxable: serviceData.is_taxable !== undefined ? serviceData.is_taxable : false,
      machinery_register_enabled: serviceData.machinery_register_enabled === true,
      pos_prices: processedPosPrices,
      machinery_assets: Array.isArray(serviceData.machinery_assets) ? serviceData.machinery_assets : [],
    });

    if (serviceData.sku) {
      setSkuPreview(serviceData.sku);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre del servicio es requerido';
    }

    if (!formData.service_type) {
      newErrors.service_type = 'El tipo de servicio es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      
      // Si cambia el tipo de servicio a "acquisition", limpiar precios
      if (name === 'service_type' && value === 'acquisition') {
        newData.has_price_list = false;
        newData.is_taxable = false;
        newData.pos_prices = {};
      }
      // Si cambia a "sale", activar automáticamente has_price_list
      else if (name === 'service_type' && value === 'sale') {
        newData.has_price_list = true;
      }
      
      return newData;
    });
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handlePriceChange = (posId, value) => {
    setFormData(prev => ({
      ...prev,
      pos_prices: {
        ...prev.pos_prices,
        [posId]: value.replace(/[^0-9.]/g, ''),
      },
    }));
  };

  const handleClassificationSelect = ({ family_id }) => {
    setFormData((prev) => ({
      ...prev,
      service_family_id: family_id || null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        name: formData.name.trim(),
        service_type: formData.service_type,
        service_family_id: servicesClassificationEnabled ? (formData.service_family_id || null) : null,
        is_active: formData.is_active,
        has_price_list: formData.service_type === 'sale' ? true : false,
        is_taxable: formData.service_type === 'sale' ? formData.is_taxable : false,
        pos_prices: formData.service_type === 'sale' ? Object.keys(formData.pos_prices).reduce((acc, posId) => {
          const price = formData.pos_prices[posId];
          if (price && parseFloat(price) > 0) {
            acc[posId] = parseFloat(price);
          }
          return acc;
        }, {}) : {},
        machinery_register_enabled: formData.machinery_register_enabled === true,
        machinery_assets: formData.machinery_register_enabled
          ? (formData.machinery_assets || []).map(({ machinery_asset_type, machinery_asset_id, usage_type }) =>
              ({ machinery_asset_type, machinery_asset_id, usage_type: usage_type || null }))
          : [],
      };

      if (formData.machinery_register_enabled) {
        submitData.machinery_events = [];
      }

      let response;
      if (service && service.id) {
        response = await serviceService.updateService(service.id, submitData);
        setToast({
          isOpen: true,
          message: 'Servicio actualizado exitosamente',
          type: 'success'
        });
      } else {
        response = await serviceService.createService(submitData);
        setToast({
          isOpen: true,
          message: 'Servicio creado exitosamente',
          type: 'success'
        });
      }

      if (response.success) {
        setTimeout(() => {
          onSuccess();
          if (!isPageMode) {
            onClose();
          }
        }, 1000);
      } else {
        throw new Error(response.message || 'Error al guardar el servicio');
      }
    } catch (error) {
      console.error('Error:', error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        setToast({
          isOpen: true,
          message: 'Por favor, corrige los errores en el formulario',
          type: 'error'
        });
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Error al guardar el servicio';
        setErrors({ submit: errorMessage });
        setToast({
          isOpen: true,
          message: errorMessage,
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isPageMode) return null;

  const formContent = (
    <>
      <form onSubmit={handleSubmit} className={isPageMode ? "p-8" : "px-6 py-4"}>
        {errors.submit && (
          <div className="mb-6 bg-red-50 text-red-700 p-4 rounded-lg text-sm border border-red-200 flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {errors.submit}
          </div>
        )}

        <div className="space-y-6">
          {/* Sección: SKU Preview - Solo mostrar en modo creación */}
          {!service && skuPreview && (
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-5 shadow-sm">
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
                SKU (Generado automáticamente)
              </label>
              <div className="text-2xl font-mono font-bold text-teal-700 mb-1">{skuPreview}</div>
              <p className="text-xs text-gray-600 mt-2">Este SKU se generará automáticamente al guardar el servicio</p>
            </div>
          )}

          {/* Sección: Información Básica */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Información Básica
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Servicio <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={200}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    errors.name ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                  }`}
                  placeholder="Ej: Servicio de Consultoría"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="service_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Servicio <span className="text-red-500">*</span>
                </label>
                <select
                  id="service_type"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  disabled={!!service}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    errors.service_type ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                  } ${service ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="acquisition">Adquisición de Servicios</option>
                  <option value="sale">Venta de Servicios</option>
                </select>
                {errors.service_type && (
                  <p className="mt-1 text-sm text-red-600">{errors.service_type}</p>
                )}
                {service && (
                  <p className="mt-1 text-xs text-gray-500">El tipo de servicio no se puede modificar</p>
                )}
              </div>

              {servicesClassificationEnabled && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clasificación del servicio
                  </label>
                  <ClassificationSelector
                    hierarchy={classificationHierarchy}
                    selectedFamilyId={formData.service_family_id}
                    onSelect={handleClassificationSelect}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Opcional. Los servicios sin familia aparecen como pendientes de clasificación.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sección: Precios - Solo se muestra si es servicio de venta */}
          {formData.service_type === 'sale' && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-blue-200">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Precios por Punto de Venta
              </h3>

              {/* Checkbox para servicio gravado */}
              <div className="mb-5">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_taxable"
                    checked={formData.is_taxable}
                    onChange={handleChange}
                    className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 focus:ring-2"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Servicio gravado (con impuestos)
                  </span>
                </label>
              </div>

              {/* Lista de precios por punto de venta */}
              {pointOfSales.length > 0 ? (
                <div className="space-y-4">
                  {pointOfSales.map((pos) => (
                    <div key={pos.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-800">{pos.name}</h4>
                          {pos.location && (
                            <p className="text-xs text-gray-500 mt-1">{pos.location}</p>
                          )}
                        </div>
                        <span className="text-xs font-medium px-2 py-1 bg-teal-100 text-teal-700 rounded">
                          {pos.code}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Precio
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="text"
                            value={formData.pos_prices[pos.id] || ''}
                            onChange={(e) => handlePriceChange(pos.id, e.target.value)}
                            className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No hay puntos de venta activos disponibles</p>
                </div>
              )}
            </div>
          )}

          {/* Sección: Opciones del Servicio */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Opciones del Servicio
            </h3>
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  Servicio activo
                </span>
              </label>
            </div>
          </div>

          {/* Módulo Maquinaria: Registrar lectura de vehículo/máquina (igual que en productos) */}
          {machineryEnabled && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2 pb-3 border-b border-gray-200">
                <span className="text-xl" aria-hidden>🚜</span>
                Registrar lectura de vehículo/máquina
              </h3>
              <label className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  name="machinery_register_enabled"
                  checked={formData.machinery_register_enabled === true}
                  onChange={(e) => setFormData((prev) => ({ ...prev, machinery_register_enabled: e.target.checked }))}
                  className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">Este servicio tiene habilitada la opción de registrar lectura de vehículo/máquina</span>
              </label>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={isPageMode ? () => window.history.back() : onClose}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (service ? 'Actualizar' : 'Crear')}
            </button>
          </div>
        </div>
      </form>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </>
  );

  if (isPageMode) {
    return formContent;
  }

  return (
    <div className={`fixed inset-0 z-50 overflow-y-auto ${isOpen ? 'block' : 'hidden'}`}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-teal-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                {service ? 'Editar Servicio' : 'Nuevo Servicio'}
              </h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {formContent}
        </div>
      </div>
    </div>
  );
};

export default ServiceForm;

